import { authError, dbError } from './logger';
import { isKnownSignedOut } from './session-presence';

interface SupabaseError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Custom Postgres SQLSTATE raised by the `enforce_check_in_cooldown` trigger when the
 * same member is checked in again within 60 seconds. Surfaced to the client as `error.code`.
 * Migration: 20260610000000_add_check_in_cooldown.sql
 */
export const CHECK_IN_COOLDOWN_ERRCODE = 'HK429';

/** User-facing message shown when a check-in is rejected by the 60-second cooldown. */
export const CHECK_IN_COOLDOWN_MESSAGE =
  'Checked in just now — please wait 60 seconds before checking in again.';

/**
 * True if the error is a check-in cooldown rejection. Recognizes both the raw PostgREST
 * error (by SQLSTATE `HK429`) and the already-wrapped Error (by its message), so it works
 * whether called on the original DB error or after a hook has re-thrown it.
 */
export function isCheckInCooldownError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  return e.code === CHECK_IN_COOLDOWN_ERRCODE || e.message === CHECK_IN_COOLDOWN_MESSAGE;
}

/**
 * True if the error is an authorization denial — Postgres `42501`
 * (insufficient_privilege). Used by hooks that read the super-admin-only
 * staff ledgers and treat a denial as "no rows for this viewer" rather than
 * a failure (the F&B report convention).
 *
 * Deliberately does NOT match `PGRST301`: that is a JWT validation failure
 * (expired/invalid session), not a permission answer — it must flow to
 * handleSupabaseError's session-expired path instead of being silently
 * converted to zero rows, which would undercount totals for a super_admin
 * whose session merely expired.
 */
export function isPermissionDenied(err: { code?: string } | null | undefined): boolean {
  return err?.code === '42501';
}

/**
 * True if PostgREST answered HTTP 401: the request carried no valid user
 * session. PostgREST sends 401 only for a bad/expired JWT or for a denial to the
 * `anon` role; a signed-in user's permission denial is a 403. So a 401 is never
 * a grant or RLS answer about the table — it is the session.
 *
 * The status is only visible on body-less errors (every HEAD count query),
 * where `withPostgrestStatusBodies` (postgrest-fetch.ts) writes it into the
 * error as `httpStatus`. Without it those errors were a bare `{ message: '' }`
 * and were filed as database errors (HOK-OS-81).
 */
export function isUnauthenticatedResponse(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  return (err as { httpStatus?: unknown }).httpStatus === 401;
}

/**
 * A read that failed only because the session was already gone. Not a fault,
 * and not something to report.
 *
 * The tell is a `42501` raised while this tab holds no session: the request
 * reached PostgREST as `anon`, and `anon` holds no grants on ANY application
 * table, so every table answers "permission denied for table X" identically.
 * That is a statement about the caller's role, not about the table.
 *
 * It happens routinely on logout. A `fetchAllRows` loop straddling the moment
 * the token is revoked gets a valid page 1 and an anon page 2; a query still
 * mounted for the few hundred milliseconds before the redirect to /login
 * commits fires into a dead session. Either way the rejection lands seconds
 * later, on a page the admin has already left. Reported, it filed six Sentry
 * issues per logout (HOK-OS-5Z, 5R, 5N, 5G, 5H, 60) — noise that would mask a
 * real grant regression on the same tables.
 *
 * A `42501` while the session is present (or its state is not yet known) is
 * still a genuine permission answer and is reported as before.
 */
export class SessionEndedError extends Error {
  constructor(message = 'Your session has ended. Please sign in again.') {
    super(message);
    this.name = 'SessionEndedError';
  }
}

/**
 * True if this error is a permission denial caused purely by the session having
 * ended — see {@link SessionEndedError}. Recognizes both the raw PostgREST
 * error and the already-wrapped form, so it works on either side of a re-throw.
 */
export function isSignedOutDenial(error: unknown): boolean {
  if (error instanceof SessionEndedError) return true;
  if (!error || typeof error !== 'object') return false;
  return (
    (isPermissionDenied(error as { code?: string }) || isUnauthenticatedResponse(error)) &&
    isKnownSignedOut()
  );
}

interface ErrorContext {
  operation: string;
  params?: Record<string, unknown>;
}

/**
 * Postgres `statement_timeout` (SQLSTATE 57014) — the server gave up on a
 * statement, it was never a fault in the request.
 *
 * The `authenticated` role on production carries `statement_timeout=8s`, so any
 * read can be cancelled when the instance stalls rather than when the query is
 * slow. HOK-OS-44 is the worked example: `get_memberships_for_members` averages
 * 73ms over 33k calls but has a 7.2s tail, and the whole instance shows the same
 * shape (realtime WAL polling peaks at 13s, storage lookups at 10s). The heaviest
 * user-facing read is simply the first to cross 8s when that happens.
 */
export const STATEMENT_TIMEOUT_ERRCODE = '57014';

/**
 * A read the database cancelled for taking too long. Retryable by definition:
 * the same statement against an unstalled instance usually returns in
 * milliseconds.
 *
 * Carries `operation` so the query cache can name what timed out if the retries
 * are exhausted — see the Sentry note on {@link isStatementTimeout}.
 */
export class StatementTimeoutError extends Error {
  constructor(public readonly operation: string) {
    super('The database is busy right now. Please try again.');
    this.name = 'StatementTimeoutError';
  }
}

/**
 * True if the error is a statement timeout. Recognizes both the raw PostgREST
 * error (by SQLSTATE `57014`) and the already-wrapped {@link StatementTimeoutError},
 * so it works on either side of a re-throw.
 *
 * Reporting rule: a timeout that is retried and then succeeds is noise and stays
 * out of Sentry. One that survives its retries is reported by the query cache's
 * `onError` — as a `warning`, not an `error`, since the fix is never in the
 * calling code. That split keeps a genuine regression (a query that now ALWAYS
 * times out) visible instead of silencing the class outright.
 */
export function isStatementTimeout(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if (error instanceof StatementTimeoutError) return true;
  const e = error as { code?: string; message?: string; name?: string };
  if (e.name === 'StatementTimeoutError') return true;
  // 57014 is `query_canceled`, which `statement_timeout` shares with
  // pg_cancel_backend() and a client's own cancel request ("canceling statement
  // due to user request"). Only the timeout is safe to swallow and retry, so the
  // code alone is not enough — match Postgres's wording for it too.
  return (
    e.code === STATEMENT_TIMEOUT_ERRCODE &&
    typeof e.message === 'string' &&
    e.message.includes('statement timeout')
  );
}

/**
 * Detects transient network errors (Safari "Load failed", Chrome "Failed to fetch")
 * and Safari-specific DOMException / StorageUnknownError from Supabase SDK.
 * These are non-actionable and should not be sent to Sentry.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    const s = String(error ?? '');
    return s === 'Load failed' || s === 'Failed to fetch' || s === 'NetworkError';
  }

  const errObj = error as Record<string, unknown>;
  const msg = String(errObj.message ?? '');

  // Safari / Chrome network errors
  if (
    msg === 'Load failed' ||
    msg === 'Failed to fetch' ||
    msg === 'NetworkError' ||
    msg.includes('TypeError: Load failed') ||
    msg.includes('TypeError: Failed to fetch') ||
    // Re-thrown form from handleSupabaseError itself — recognize the helper's
    // own translation so outer catches don't re-log the same network failure.
    msg.includes('A network error occurred. Please check your connection')
  ) {
    return true;
  }

  // Safari DOMException via Supabase Storage SDK (StorageUnknownError wrapping DOMException)
  if (
    errObj.name === 'StorageUnknownError' ||
    errObj.originalError instanceof DOMException ||
    msg.includes('did not match the expected pattern')
  ) {
    return true;
  }

  return false;
}

/**
 * Handles standard Supabase query errors with consistent JWT/generic error handling.
 *
 * Replaces the repeated if/else JWT check pattern across all hooks:
 * - JWT expired → logs authError + throws session expired message
 * - Other errors → logs dbError + throws user-friendly message
 *
 * @throws Always throws — either a session expired or a "Failed to {operation}" error.
 */
export function handleSupabaseError(
  error: SupabaseError,
  { operation, params }: ErrorContext,
): never {
  if (isTransientNetworkError(error)) {
    // Log to console only — not Sentry (these are non-actionable)
    console.warn(`[Network] Transient error during ${operation}`, params ?? '');
    throw new Error('A network error occurred. Please check your connection and try again.');
  }

  // The database cancelled the statement. Console only here: this throw is the
  // attempt React Query is about to retry, and the retry usually succeeds. If it
  // does not, the query cache reports the exhausted timeout to Sentry once.
  if (isStatementTimeout(error)) {
    console.warn(`[Database] Statement timeout during ${operation}`, params ?? '');
    throw new StatementTimeoutError(operation);
  }

  // The session ended underneath this read — the request went out as `anon`.
  // Console only, never Sentry: see SessionEndedError for why.
  if (isSignedOutDenial(error)) {
    console.warn(`[Auth] Session ended during ${operation}`, params ?? '');
    throw new SessionEndedError();
  }

  if (error.message === 'JWT expired') {
    authError(`JWT expired while ${operation}`, error, params);
    throw new Error('Your session has expired. Please sign in again.');
  } else if (isUnauthenticatedResponse(error)) {
    authError(`Session rejected (HTTP 401) while ${operation}`, error, params);
    throw new Error('Your session has expired. Please sign in again.');
  } else {
    dbError(`Error ${operation}`, error, params);
    throw new Error(`Failed to ${operation.replace(/^(fetching|loading|checking|creating|updating|deleting)/, (m) => {
      const map: Record<string, string> = {
        fetching: 'load',
        loading: 'load',
        checking: 'check',
        creating: 'create',
        updating: 'update',
        deleting: 'delete',
      };
      return map[m] || m;
    })}`);
  }
}

/**
 * Handles unexpected errors in catch blocks with consistent logging and re-throwing.
 *
 * Replaces the repeated 5-line catch block pattern across all hooks:
 * - If the error is already an Error instance, re-throws its message
 * - Otherwise logs and throws a generic "unexpected error" message
 *
 * @throws Always throws — preserves original Error messages, wraps unknown errors.
 */
/**
 * A refusal the user is meant to READ, not an incident to investigate.
 *
 * The database distinguishes these by SQLSTATE 22023 — a rule the caller broke, with
 * a sentence written for a human. Without a way to say so, such a refusal reaches
 * `handleUnexpectedError` and files a Sentry issue, so an ordinary "that member
 * already has a booth then" at the front desk looks identical to a fault. Throw this
 * instead and it reaches the user's toast untouched and unreported.
 */
export class RuleRejectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleRejectionError';
  }
}

export function handleUnexpectedError(
  error: unknown,
  { hookName, operation, params }: { hookName: string; operation: string; params?: Record<string, unknown> },
): never {
  // An expected refusal, already phrased for the reader. Pass it through without
  // reporting: it is the system working.
  if (error instanceof RuleRejectionError) {
    throw error;
  }

  // The session ended mid-read. `handleSupabaseError` has already logged it to
  // the console; re-reporting here is what turned one logout into two Sentry
  // issues per failing query (the raw captureMessage AND this captureException).
  if (isSignedOutDenial(error)) {
    throw error instanceof SessionEndedError ? error : new SessionEndedError();
  }

  // Already console-warned by handleSupabaseError on the way out. Re-reporting
  // here is what turned one failure into two Sentry issues for the signed-out
  // case above; a timeout is no different.
  if (isStatementTimeout(error)) {
    throw error instanceof StatementTimeoutError ? error : new StatementTimeoutError(operation);
  }

  if (isTransientNetworkError(error)) {
    console.warn(`[Network] Transient error in ${hookName}`, params ?? '');
    throw new Error('A network error occurred. Please check your connection and try again.');
  }

  dbError(`Unexpected error in ${hookName}`, error, params);
  throw new Error(
    error instanceof Error
      ? error.message
      : `An unexpected error occurred while ${operation}`
  );
}

/**
 * Maps Supabase errors to user-friendly Error objects without throwing.
 *
 * Unlike `handleSupabaseError` (which always throws), this returns an Error
 * instance — useful when the caller needs to throw it themselves or pass it
 * along (e.g. in React Query error boundaries that expect Error objects).
 *
 * Handles:
 * - JWT expired → session expired message
 * - Known operation contexts → specific user-friendly messages
 * - Fallback messages based on operation keywords (fetch/create/update/delete)
 */
export function mapSupabaseError(
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): Error {
  if (!error) {
    return new Error('Unknown error occurred');
  }

  const errorObj = error as { message?: string };

  // Handle transient network errors (no Sentry)
  if (isTransientNetworkError(errorObj)) {
    console.warn(`[Network] Transient error in ${context}`, metadata ?? '');
    return new Error('A network error occurred. Please check your connection and try again.');
  }

  // Cancelled by statement_timeout — retryable, reported only once exhausted.
  if (isStatementTimeout(error)) {
    console.warn(`[Database] Statement timeout in ${context}`, metadata ?? '');
    return new StatementTimeoutError(context);
  }

  // Session ended mid-read — an `anon` denial, not a permission answer.
  if (isSignedOutDenial(error)) {
    console.warn(`[Auth] Session ended in ${context}`, metadata ?? '');
    return new SessionEndedError();
  }

  // Handle JWT expiration errors
  if (errorObj.message === 'JWT expired') {
    authError(`JWT expired in ${context}`, error, metadata);
    return new Error('Your session has expired. Please sign in again.');
  }

  // A body-less 401 (HEAD count query) — the same dead session, see isUnauthenticatedResponse.
  if (isUnauthenticatedResponse(error)) {
    authError(`Session rejected (HTTP 401) in ${context}`, error, metadata);
    return new Error('Your session has expired. Please sign in again.');
  }

  // Handle generic database errors
  dbError(`Error in ${context}`, error, metadata);

  // Return user-friendly error message based on context
  const errorMessage = getContextualErrorMessage(context, errorObj);
  return new Error(errorMessage);
}

/**
 * Gets contextual error messages based on the operation context
 */
function getContextualErrorMessage(context: string, error: { message?: string }): string {
  const contextMessages: Record<string, string> = {
    'fetching webcam photos': 'Failed to fetch member photos',
    'fetching profile photos': 'Failed to fetch member profile photos',
    'fetching all webcam photos': 'Failed to fetch member photos',
    'fetching all profile photos': 'Failed to fetch member profile photos',
    'creating webcam photo': 'Failed to create photo record',
    'updating profile photo': 'Failed to update profile photo',
    'deleting photo': 'Failed to delete photo',
  };

  // Try to find a specific message for the context
  const specificMessage = contextMessages[context.toLowerCase()];
  if (specificMessage) {
    return specificMessage;
  }

  // Fallback to generic messages based on operation type
  if (context.includes('fetch') || context.includes('load')) {
    return 'Failed to load data. Please try again.';
  }

  if (context.includes('create') || context.includes('insert')) {
    return 'Failed to create record. Please try again.';
  }

  if (context.includes('update') || context.includes('edit')) {
    return 'Failed to update record. Please try again.';
  }

  if (context.includes('delete') || context.includes('remove')) {
    return 'Failed to delete record. Please try again.';
  }

  // Ultimate fallback
  return error.message || 'An unexpected error occurred. Please try again.';
}
